import { clientIdentityHeaders, withClientIdentity } from '@/lib/clientIdentity'

// `fetch`'s own parameter types, named through `fetch` because the lint config does not know the
// DOM lib's type-only globals (RequestInfo, RequestInit).
export type ApiFetchInput = Parameters<typeof fetch>[0]
export type ApiFetchInit = NonNullable<Parameters<typeof fetch>[1]>

/**
 * `fetch` for requests to this app's OWN API that cannot go through the axios instance — the
 * offline-queue replay, multipart uploads that need the browser to set the boundary, the
 * maintenance poll. It adds the client identity headers (see clientIdentity.ts) and is otherwise
 * exactly `fetch`: same arguments, same response, same errors.
 *
 * Deliberately NOT for media, `/version.json`, `/native-version.json` or the connectivity and
 * latency probes: those are not API work, and a probe must measure the link, not this wrapper.
 * `__tests__/apiFetch.inventory.test.ts` keeps the list of raw `fetch` callers honest.
 *
 * The global `fetch` is looked up per call, not captured at import, so tests that spy on it and
 * the dev harness transport that wraps it still see every request.
 */
export async function apiFetch(input: ApiFetchInput, init: ApiFetchInit = {}): Promise<Response> {
  const identity = await clientIdentityHeaders()
  // A Request's own headers are replaced wholesale by `init.headers`, so start from them when the
  // caller passed none — otherwise adding three headers would silently drop all the others.
  const base = init.headers ?? (input instanceof Request ? input.headers : undefined)

  return fetch(input, { ...init, headers: withClientIdentity(base, identity) })
}
